<template>
  <div class="flex flex-col items-stretch justify-start">
    <SloLoader :loading="loading" size="xl  ">
      <div v-if="showForm === 'validate'">
        <form
          @submit.prevent="handleContinue"
          action="#"
          class="marketplace-checkout--contact-information-form flex flex-col items-stretch justify-start"
        >
          <div class="marketplace-checkout--table">
            <div class="marketplace-checkout--table-header">
              <h2 class="text-xl fw-strong">{{ 'Contact Information' | t }}</h2>
            </div>
            <div v-if="proceedCheck" class="marketplace-checkout--table-body flex items-start justify-between gap-md padding-full">
              <p class="flex items-start justify-start flex-wrap gap-sm m-0 m-0 c-gray-700">
                <span class="text-base fw-strong c-gray-900">Contact</span>
                <span>{{ email }}</span>
              </p>
              <SloButton
                @click="proceedCheck = false"
                fill="clear"
                class="p-0 c-blue-400 hover:c-blue-700"
              >
                {{ 'Change' | t }}
              </SloButton>
            </div>
            <div v-else class="marketplace-checkout--table-body padding-full">
              <SloField class="w-100">
                <SloLabel for="email" :error="emailError">{{ 'Email' | t }}</SloLabel>
                <SloInput
                  id="email"
                  type="email"
                  name="email"
                  v-model="email"
                  :error="emailError"
                  :readonly="proceedCheck"
                />
              </SloField>
              <div class="slo-auth-extra">
                <SloField>
                  <SloInput
                    v-model="marketingEmailSignUp"
                    :checked="marketingEmailSignUp"
                    type="checkbox"
                  />
                  <SloLabel>{{ 'Yes, sign me up to receive emails from Sloan' | t  }}</SloLabel>
                </SloField>
              </div>
            </div>
          </div>
          <SloButton
            v-if="!proceedCheck"
            :disabled="loading || !email" type="submit"
            class="fixed-button-width self-end mt-lg"
          >
            <SloLoader :loading="loading" color="white" size="md">
              {{ 'Continue' | t }}
            </SloLoader>
          </SloButton>
          <div
            v-if="proceedCheck"
            class="self-end flex items-center justify-start flex-wrap gap-md w-100 mt-lg md:w-max"
          >
            <SloButton @click.prevent="handleContinueAsGuest" fill="outline" type="button" class="flex-1">
              <SloLoader :loading="loading" color="blue" size="md">
                {{ 'Checkout as Guest' | t }}
              </SloLoader>
            </SloButton>
            <SloButton @click.prevent="showForm = 'register'" type="button" class="flex-1">
              {{ 'Create Account' | t }}
            </SloButton>
          </div>
        </form>
      </div>
      <div v-if="showForm === 'login'" class="marketplace-checkout--login-table">
        <div class="marketplace-checkout--table-header">
          <h2 class="text-xl fw-strong">{{ 'Contact Information' | t }}</h2>
        </div>
        <div class="marketplace-checkout--table-body">
          <SloAuthLogin :email="email" />
        </div>
      </div>
      <div v-if="showForm === 'register'" class="marketplace-checkout--register-table">
        <div class="marketplace-checkout--table-header">
          <h2 class="text-xl fw-strong">{{ 'Contact Information' | t }}</h2>
        </div>
        <div class="marketplace-checkout--table-body">
          <SloAuthRegister :email="email" :marketing-email-signup="marketingEmailSignUp" />
        </div>
      </div>
    </SloLoader>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {mapGetters} from 'vuex';
import {authApi} from '../apis/auth';
import {marketplaceApi} from '../apis/marketplace';
import SloAuthLogin from '../../../lib/components/SloAuthLogin.vue';
import SloAuthRegister from '../../../lib/components/SloAuthRegister.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import {marketingEmailSignup} from '../../main/utils';

export default {
  name: 'CheckoutStep1',
  components: {
    SloAuthLogin,
    SloAuthRegister,
    SloButton,
    SloField,
    SloLabel,
    SloInput,
    SloLoader
  },
  filters: {
    t
  },
  data() {
    return {
      loading: false,
      email: '',
      emailError: false,
      marketingEmailSignUp: true,
      showForm: 'validate',
      proceedCheck: false
    };
  },
  created() {
    if (this.authenticated) this.$emit('continue');
  },
  computed: mapGetters('auth', ['authenticated']),
  watch: {
    authenticated(newValue) {
      if (newValue) {
        this.step = 2;
        this.$snotify.clear();
      }
    }
  },
  methods: {
    async handleContinue() {
      this.loading = true;
      this.emailError = false;

      await authApi.validate(this.email)
        .then(res => {
          if (res?.data?.active) {
            this.loading = false;
            this.showForm = 'login';
            this.$snotify.info(
              t('User account exists, please login'),
              {timeout: 4000}
            );
            window.scrollTo(0, 0);
          }
        })
        .catch(e => {
          const errors = e?.response?.data?.errors;

          if (errors[0]?.reason === 'invalid') {
            this.loading = false;
            this.emailError = errors.find(error => error.subject === 'email')?.message;
            this.$snotify.error(t(errors[0]?.message));
            window.scrollTo(0, 0);
            return;
          }

          this.loading = false;
          this.proceedCheck = true;
          this.$snotify.info(
            t(errors[0]?.message),
            {timeout: 4000}
          );
          window.scrollTo(0, 0);
        });
    },
    async handleContinueAsGuest() {
      this.loading = true;
      this.emailError = false;

      await marketplaceApi.attachGuestEmailToCart(this.email)
        .then(() => {
          if (this.marketingEmailSignUp) {
            marketingEmailSignup({email: this.email});
          }

          this.$emit('continue');
        })
        .catch(e => {
          const errors = e?.response?.data?.errors;
          this.loading = false;
          this.emailError = errors ?? errors.find(error => error.subject === 'email')?.message;
          this.$snotify.error(t(errors[0]?.message));
          window.scrollTo(0, 0);
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.fixed-button-width {
  min-width: 96px;
}

.marketplace-checkout--login-table, .marketplace-checkout--register-table {
  .marketplace-checkout--table-header {
    border: 1px solid $color-gray-400;
    border-radius: 1.2rem 1.2rem 0 0;
  }
}

:deep(.slo-auth-login) {
  display: grid;

  > label {
    margin: 0 !important;
    padding: 1.6rem 1.6rem 0;
    border-inline: 1px solid $color-gray-400;

    @media (min-width: $screen-md) {
      padding: 2.4rem 2.4rem 0;
    }
  }

  > div {
    margin: 0 !important;
    padding: 1.6rem;
    border-inline: 1px solid $color-gray-400;
    border-bottom: 1px solid $color-gray-400;
    border-radius: 0 0 1.2rem 1.2rem;

    @media (min-width: $screen-md) {
      padding: 2.4rem;
    }
  }

  .slo-button {
    place-self: end;
    width: auto;
    min-width: 80px;
    margin-top: 3.2rem !important;
  }
}

:deep(.slo-auth-register) {
  margin-block-start: 0 !important;
  display: grid;

  > label {
    margin: 0 !important;
    padding: 1.6rem 1.6rem 0;
    border-inline: 1px solid $color-gray-400;

    @media (min-width: $screen-md) {
      padding: 2.4rem 2.4rem 0;
    }
  }

  > div.slo-auth-password-check {
    margin: 0 !important;
    padding: 1.6rem 1.6rem 0;
    border-inline: 1px solid $color-gray-400;

    @media (min-width: $screen-md) {
      padding: 2.4rem 2.4rem 0;
    }
  }

  > div.slo-auth-extra {
    margin: 0 !important;
    padding: 1.6rem;
    border-inline: 1px solid $color-gray-400;
    border-bottom: 1px solid $color-gray-400;
    border-radius: 0 0 1.2rem 1.2rem;

    @media (min-width: $screen-md) {
      padding: 2.4rem;
    }
  }

  .slo-button {
    place-self: end;
    width: auto;
    min-width: 134px;
    margin-top: 3.2rem !important;
  }
}

.slo-auth-extra {
  :deep(.slo-field) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .slo-label {
    margin-top: 0 !important;
    font-weight: normal;
    font-size: $font-size-small;
    color: $color-gray-900;
  }
}
</style>
